/**
 * Authentication Context Provider
 * =============================================================================
 * Manages global authentication state using Firebase Auth
 *
 * Features:
 * - Tracks current user and auth state
 * - Provides login/logout functions
 * - Automatically refreshes ID tokens
 * - Handles auth state changes
 * - Stores tokens in memory (secure - not in localStorage)
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { logger } from '../lib/logger';
import { purgeAllChatSnapshots, purgeShellForUid } from '../utils/chatHistoryStore';

// Token refresh interval (45 minutes - tokens expire after 1 hour)
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  idToken: string | null;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);

  /**
   * Get fresh ID token from current user
   */
  const refreshToken = async (): Promise<string | null> => {
    try {
      if (!auth.currentUser) {
        console.warn('⚠️ No authenticated user to refresh token');
        return null;
      }

      // Force refresh to get a new token
      const token = await auth.currentUser.getIdToken(true);
      setIdToken(token);
      console.log('✅ Token refreshed successfully');
      return token;
    } catch (err) {
      console.error('❌ Error refreshing token:', err);
      setError('Failed to refresh authentication token');
      return null;
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async (): Promise<void> => {
    try {
      const signingOutUid = auth.currentUser?.uid ?? null;
      await firebaseSignOut(auth);
      // Stored CEPI conversations are the signed-out user's tenant data. They
      // are already unreadable by anyone else (keys are scoped by uid + org),
      // but they must not stay at rest in a tab someone else may sign into.
      purgeAllChatSnapshots();
      purgeShellForUid(signingOutUid);
      setIdToken(null);
      setUser(null);
      setError(null);
      console.log('✅ User signed out successfully');
    } catch (err: any) {
      console.error('❌ Error signing out:', err);
      setError(err.message || 'Failed to sign out');
      throw err;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    console.log('🔄 AuthContext: SINGLE listener starting');

    // The uid this tab was last signed in as. Several code paths sign out by
    // calling Firebase directly (LoginPage, FinishSignInPage, authApi) rather
    // than through signOut() above, so the purge also runs here: whenever the
    // signed-in uid goes away or changes, drop every stored CEPI conversation.
    // Starts null, so the initial restore of a session on page load (null ->
    // uid) is NOT treated as a change and a reload keeps the user's history.
    let lastUid: string | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const timestamp = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
      logger.debug(`[${timestamp}] 🎯 Auth → ${firebaseUser?.email || 'NULL'}`);

      const uid = firebaseUser?.uid ?? null;
      if (lastUid !== null && lastUid !== uid) {
        purgeAllChatSnapshots();
        purgeShellForUid(lastUid);
      }
      lastUid = uid;

      if (firebaseUser) {
        setUser(firebaseUser);
        setError(null);
        firebaseUser.getIdToken(true).then(token => {
          logger.debug(`[${timestamp}] ✅ Token → ${firebaseUser.email}`);
          setIdToken(token);
        }).catch(tokenErr => {
          console.error(`[${timestamp}] ❌ Token failed →`, tokenErr);
        });
      } else {
        setUser(null);
        setIdToken(null);
        setError(null);
      }
      setLoading(false);
    });

    return () => {
      console.log('🧹 AuthContext: Listener cleaned');
      unsubscribe();
    };
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (!user) {
      console.log('⏰ No user - skipping token refresh setup');
      return;
    }

    logger.debug('⏰ Setting up automatic token refresh for user:', user.email);

    // Refresh token periodically
    const intervalId = setInterval(async () => {
      console.log('🔄 Auto-refreshing token...');
      await refreshToken();
    }, TOKEN_REFRESH_INTERVAL);

    // Cleanup interval on unmount or user change
    return () => {
      console.log('🧹 Cleaning up token refresh interval');
      clearInterval(intervalId);
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    idToken,
    signOut,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 * Throws error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
